import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { resetStack } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { isTypeFn } from '@sorare/core/src/lib/gql';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { GameBoxScore } from '../GameBoxScore';
import type { GameView_GameOfBaseball } from './__generated__/index.graphql';

const Wrapper = styled(Vertical).attrs({ gap: 0 })`
  padding: var(--unit);
  --team-logo-width: var(--quadruple-unit);
  --team-score-width: calc(7 * var(--unit));
  ${resetStack}

  @media ${tabletAndAbove} {
    padding: var(--triple-unit);
  }
`;

type Props = {
  game: GameView_GameOfBaseball;
};

export const GameView = ({ game }: Props) => {
  if (!game) {
    return null;
  }

  const homeTeamPlayers = game.anyPlayers
    .filter(isTypeFn('BaseballPlayer'))
    .filter(
      p =>
        p.playerGameScore?.anyPlayerGameStats.anyTeam.slug ===
        game.homeTeam?.slug
    );

  const awayTeamPlayers = game.anyPlayers
    .filter(isTypeFn('BaseballPlayer'))
    .filter(
      p =>
        p.playerGameScore?.anyPlayerGameStats.anyTeam.slug ===
        game.awayTeam?.slug
    );

  return (
    <Wrapper>
      <GameBoxScore
        game={game}
        homeTeamPlayers={homeTeamPlayers}
        awayTeamPlayers={awayTeamPlayers}
      />
    </Wrapper>
  );
};

GameView.fragments = {
  GameOfBaseball: gql`
    fragment GameView_GameOfBaseball on GameOfBaseball {
      id
      awayScore
      statusTyped
      awayTeam {
        slug
        name
        shortName
        pictureUrl
      }
      homeScore
      homeTeam {
        slug
        name
        shortName
        pictureUrl
      }
      mySo5Appearances {
        id
        ...GameBoxScore_So5Appearance
      }
      ...GameBoxScore_GameOfBaseball
      anyPlayers {
        slug
        ...GameBoxScore_BaseballPlayer
        playerGameScore(gameId: $gameIdWithoutPrefix) {
          id
          anyPlayerGameStats {
            id
            anyTeam {
              slug
            }
          }
        }
      }
    }
    ${GameBoxScore.fragments.So5Appearance}
    ${GameBoxScore.fragments.BaseballPlayer}
    ${GameBoxScore.fragments.GameOfBaseball}
  ` as TypedDocumentNode<GameView_GameOfBaseball>,
};
